import forms from '@/translations/en/sites/site-forms';
import dateChangeReason from '@/translations/en/sites/date-change-reason';

export default {
  forms,
  dateChangeReason,
  // Legacy translations; do not add to below list
  title: 'Create new site',
  createProject: 'Create new project',
  locationInfoSubtitle: 'Provide location information related to this site.',
  fundingInfoSubtitle: 'Provide funding information related to this site.',
  basicInfo: 'Basic Information',
  basicInfoSubtitle: 'Provide the basic project information for this site.',
  required: 'Required fields are marked by *',
  clientInfo: {
    title: 'Client information',
    subtitle: 'Provide client information and details.',
  },
  siteContract: {
    title: 'Site Contract',
    subtitle: 'Contract information related to this site.',
    setContract: 'Set a site contract type',
    fixedCost: 'BOQ line items have no markup set, contract is set at an agreed upon value.',
    costPlus:
      'BOQ items have a mark up percentage, you can alter individual markups within your BOQ claim view.',
    noContract: 'This site does not contain a contract. No markup is added.',
    remeasurable: 'Remeasurable',
    targetProfitMargin: {
      title: 'Set your targeted profit margin amount',
      subtitle:
        'Your profit margin will be the difference between your site contract budget and your BOQ value.',
    },
    defaultMarkup: {
      title: 'Set your default contract markup percentage',
      subtitle:
        'Your markup percentage will be added to the selling price of all goods and services added to your BOQ. This can be toggled on and off to view in the BOQ.',
    },
    changeMarkupModal: {
      title: 'Change Sites Markup Value',
      content:
        'You’ve changed the sites default markup value. Do you wish to override all manually updated markups on line items with this change?',
      additionalInfo:
        'Selecting “Yes” will set ALL line items to your default markup value. This action is irreversible.',
      confirm: 'Yes, Override',
      cancel: 'No, Keep',
    },
  },
  users: {
    title: 'Users',
    subtitle: 'Select the users involved in this project.',
    removed: {
      title: 'User Removed',
    },
    roleChangeFailed: {
      title: 'Could not update role',
      content: 'Something went wrong changing this role — please try again.',
    },
    alreadyAdded: {
      title: 'User Already Added',
      content: '{email} has already been invited to this site as {role}.',
    },
  },
  locationInfo: 'Location Information',
  funding: 'Funding',
  form: {
    siteName: 'Site name',
    siteCode: 'Site short code',
    siteErfNumber: 'Site ERF number',
    project: 'Project',
    status: 'Site status',
    commencementDate: 'Planned commencement date',
    completionDate: 'Planned completion',
    company: 'Company',
    private: 'Private client',
    noClient: 'No client',
    companyName: 'Company name',
    companyVAT: 'Company VAT no.',
    clientName: 'Client name',
    contactNumber: 'Client contact no.',
    salesRepNumber: 'Sales Rep no.',
    email: 'Client email',
    enterSiteAddress: 'Enter site address',
    enterDeliveryAddress: 'Enter delivery address',
    siteAddress: 'Site Address',
    newAddress: 'Search new address',
    manualAddress: 'Enter manually',
    deliveryAddress: 'Delivery Address',
    deliverySameSite: 'Delivery address same as site',
    bankFunding: 'Bank funding',
    selectBank: 'Select a bank',
    loanAmount: 'Loan amount',
    loanAccount: 'Loan account number',
    selfFunding: 'Self funding',
    selfFundingAmount: 'Self funding amount',
    total: 'Total',
    totalFunding: 'Total funding',
    siteAdminRequired: '*At least one site admin is required',
    siteCodeExists: 'Site code already exists in this organisation',
    siteErfNumberExists: 'A site with this ERF number already exists in this project',
    siteCodeInfoEmpty:
      'The short code is a {min} or {max} character code used on orderID and paymentID codes. Eg. OR010125-{code}-423',
    siteCodeExample: 'A43',
    siteErfNumberExample: '(eg. ERF_123)',
    siteStatusTooltip:
      'Site status transitions automatically. Once a linked Order or Claim is Approved, Partially Delivered, Delivered, or Complete, the site moves to Live. A site in Live or Closed status cannot be manually returned to Planning.',
  },
  editSite: {
    title: 'Site Details',
    unsavedChangesModal: {
      title: 'Discard changes?',
      content: "You have unsaved changes. They'll be lost if you continue.",
      confirmLabel: 'Discard & Close',
      cancelLabel: 'Keep Editing',
    },
  },
  updateSiteCodeModal: {
    title: 'Change site short code',
    content:
      'Changing the site short code will only apply to any orders and payment created AFTER the change. Historical orders and payments will still contain the old short code. \n\nAre you sure?',
    warning: 'This action is irreversible',
    confirm: 'Yes, Change',
  },
  newProjectModal: {
    title: 'New Project',
    form: {
      details: 'Enter project details below',
      name: 'Project name',
      addUsers: 'Add project manager/s',
      create: 'Create project',
    },
  },
  createProjectSuccess: {
    title: 'Project Created',
    content: 'Project has been successfully created.',
  },
  createSite: 'Create site',
  openNewSite: 'Open new site',
  createSiteSuccess: {
    title: 'Site Created',
    content: 'Site has been successfully created.',
  },
  createSiteNoProjectPermission: {
    title: 'You cannot add a site to this project',
    content: 'Choose a project you manage, or ask an organisation admin to create the site.',
  },
  noSites: {
    line1: 'You currently have no sites within this project, or they may be in an archived state.',
  },
  emptyState: {
    title: 'No sites yet',
    filteredTitle: 'No sites matching your filters',
    filteredDescription: 'Try adjusting or clearing your filters to see more.',
  },
  tableData: {
    headers: {
      archived: 'Archived',
      siteName: 'Site name',
      risk: 'Risk',
      people: 'People',
      budgetVsTimeline: 'Budget vs Timeline',
      siteDates: 'Site dates',
      available: 'Available',
      spent: 'Spent',
      remaining: 'Remaining',
      context: 'Context',
      select: 'Select',
    },
    atRisk: 'At risk',
    onTrack: 'On track',
    viewBoq: 'View bill of quantities for {name}',
    percentageBudgetUsed: '{percentage}% budget used',
    daysRemaining: '{days} days remaining',
    daysOverdue: '{days} days overdue',
    closed: 'Closed',
    dueToday: 'Due today',
    closedOn: 'Site closed on {date}',
  },
  contextMenu: {
    edit: 'Edit site',
    createClientVariation: 'Create client variation',
    view: 'View site details',
    archive: 'Archive site',
    unarchive: 'Unarchive site',
  },
  archiveSiteModal: {
    archiveContent:
      'This will move this site to an ‘Archived’ state, hiding it from view. You can view any archived sites by checking the “View archived sites” toggle on the sites screen.',
    unarchiveContent: 'Are you sure you want to unarchive this site?',
    confirm: 'Confirm',
    archive: 'archive',
    unarchive: 'unarchive',
    siteCodeMessage: 'This site does not have a site code, please add one before archiving.',
  },
  updateSiteSuccess: {
    title: 'Site Updated',
    content: 'Site has been successfully updated',
  },
  addToBOQSidebar: {
    title: 'Add item to site BOQ',
    completeDetails: 'Complete the details below',
    form: {
      project: 'Project',
      site: 'Site',
      section: 'Section',
      selectSubSection: 'Select sub-section',
      lineName: 'Line name',
      selectLineName: 'Select line name',
      qty: 'Qty',
      addedQty: 'Added Qty',
      rate: 'Rate',
    },
    itemExists: 'This item already exists in the selected BOQ section.',
    goToBoq: 'Go to site BOQ',
    radioButtonOptions: {
      title: 'Select one to apply',
      new: 'Add as new line',
      existing: 'Add to existing item line',
    },
  },
  boq: {
    addToBoq: 'Add to site BOQ',
    costBOQ: 'Cost BOQ',
    sellingBOQ: 'Selling BOQ',
    sellingBOQLoadingModalText: 'Fetching selling BOQ\nThis could take a few seconds...',
    editSellingBOQModal: {
      title: 'Unable to Edit Selling BOQ',
      content:
        'You’re currently viewing the Selling view of your BOQ (Read only). Would you like to switch to the Cost view of your BOQ to make changes to items?',
      confirm: 'Yes, Switch to Cost',
    },
    exportConfirm: {
      content: 'Select which version of the BOQ you want to export.',
      costInfo: 'Exported at cost values',
      sellingInfo: 'Exported with selling values',
      exportNow: 'Export now',
    },
    exportBOQ: {
      title: 'Export BOQ',
      loadingText: 'Preparing file for export\nThis could take a few seconds...',
      exportSuccess: {
        title: 'Export successful',
        content: 'BOQ has been successfully exported',
      },
      exportFailed: {
        title: 'Export failed',
        content: 'The BOQ could not be exported. Try again in a moment.',
      },
    },
    exportDisable:
      'Selling BOQ export is unavailable. You may not have the necessary permissions or an export is not currently available.',
    sectionItems: '{number} item',
    sectionItemsMultiple: '{number} items',
    addItemSpec: 'Add item spec',
    typeFilter: 'Type',
    vendorFilter: 'Vendor',
    info: 'Can’t find the item spec you’re looking for?',
    addCustomItems: {
      back: 'Back to item search',
      selectVendor: {
        addNewVendor: '+ Add new vendor',
      },
    },
    addItems: 'Add custom items to vendors',
    lastPaidPrice: 'Last paid {amount} /{type}',
    editBill: 'Edit bill',
    closeEditBill: 'Lock bill',
    claimView: 'Switch to claim view',
    billView: 'Switch to bill view',
    addSection: 'Add section',
    addSubSection: 'Add sub-section',
    addGroup: 'Add group',
    addGroupItem: 'Add group item',
    addLineItem: 'Add line item',
    groupSelectedItems: 'Group selected items',
    addItemPlaceholder: 'Type line item name and press Enter or Tab (Esc to close)',
    syncVendorModal: {
      title: 'Sync vendor',
      content1:
        'In order to add this item spec, you need to sync your organisation with this vendor.',
      content2: 'Are you sure you wish to sync with {0} vendor?',
      confirm: 'Yes, sync now',
    },
    addGroupModal: {
      title: 'Group BOQ Line items in {type}',
      noItemsTitle: 'Create new {type} group',
      editTitle: 'Update {type} group',
      form: {
        name: 'Group name',
        description: 'Group description',
        groupUnit: 'Group unit',
        unitAmount: 'Unit quantity',
        create: 'Create group and close',
        createAndNew: 'Create group and new',
      },
      addGroupSuccess: {
        title: 'New group added',
        content: 'New group has been added successfully',
      },
    },
    addGroupErrorModal: {
      title: 'Selected items cannot be added to group',
      notLineItem: {
        line1: 'One or more of the selected items is already in a group.',
        line2: 'Only items not already in a group can be added to a new group.',
      },
    },
    addSectionModal: {
      title: 'New {type}',
      details: '{type} Details',
      form: {
        sectionTitle: 'Section title',
        budgetThreshPercentage: 'Budget threshold percentage',
        info: 'Set budget thresholds to flag when a section or sub-section goes over the assigned percentage of the budget.',
        createSection: 'Create {type} and close',
        createSectionAndNew: 'Create {type} and new',
        // The order flow's dialog creates one section and closes, so it drops the "and close"
        // qualifier the BOQ builder needs to distinguish its two buttons.
        createSectionOnly: 'Create {type}',
        budgetThreshPlaceholder: 'Set a budget threshold percentage',
      },
      addSectionSuccess: {
        title: 'New {type} added',
        content: 'New {type} has been added successfully',
      },
      updateSectionGroupSuccess: {
        title: '{type} updated',
        content: '{type} has been updated successfully',
      },
    },
    deleteSectionGroupModal: {
      title: 'Delete {type}',
      content1: 'Are you sure you wish to delete this {type}?',
      content2: 'All items listed under this {type} will be removed.',
      deleteSectionGroupSuccess: {
        title: '{type} deleted',
        content: '{type} has been deleted successfully',
      },
    },
    deleteTemplateModal: {
      title: 'Delete template',
      content1: 'Are you sure you want to remove template "{name}"?',
      contentOrg: 'If you continue, this template will be removed for your entire organisation.',
      contentBoq:
        'If you continue, this template will be removed for all organisations using Budget Control by BOQNET.',
      deleteTemplateSuccess: {
        title: 'Template deleted',
        content: 'Template has been deleted successfully',
      },
    },
    deleteBOQModal: {
      title: 'Delete BOQ',
      content: 'Are you sure you want to delete this BOQ?',
      deleteBOQSuccess: {
        title: 'BOQ deleted',
        content: 'BOQ has been deleted successfully',
      },
    },
    saveAsTemplateModal: {
      title: 'Create a template from this BOQ',
      body: 'Saving a BOQ template will allow you to use the same sections and titles for future Bill’s of Quantity.',
      form: {
        name: 'Template name',
        makePublic: 'Make this template available to all organisations',
        saveTemplate: 'Save template',
        includeDetailsLabel: 'Include item details',
        includeDetailsDescription:
          'Saves vendor, specification, quantities, rates, and unit types alongside the BOQ structure',
      },
      existingTemplates: 'Existing templates',
      publicTemplate: 'Public template',
      saveAsTemplateSuccess: {
        title: 'Template created',
        content: 'A template of this BOQ has been created successfully',
      },
      updateTemplateAvailablity: {
        title: 'Template updated',
        contentPublic: 'Template is available to all organisations.',
        contentNotPublic: 'Template is only available to this organisation.',
      },
      templateType: {
        StructureOnly: 'Structure only',
        Detailed: 'Detailed',
      },
      publicTemplateDeleteTooltip: 'A public template can only be deleted by a BOQ Admin',
    },
    addLineItemSuccess: {
      title: 'Item added',
      content: 'Item has been added successfully',
    },
    updateLineItem: {
      form: {
        addRef: 'Add reference',
        addSpec: 'Add specification',
        addVendor: 'Add vendor',
      },
      updateLineItemSuccess: {
        title: 'Item updated',
        content: 'Item has been updated successfully',
      },
    },
    updateVariationStatusModal: {
      updateVariationStatusSuccess: {
        title: 'Variation {update}',
        content: 'Variation has been successfully {update}',
      },
    },
    deleteLineItemModal: {
      title: 'Delete line item',
      content: 'Are you sure you want to delete this line item?',
      deleteLineItemSuccess: {
        title: 'Item deleted',
        content: 'Item has been deleted successfully',
      },
    },
    tabs: {
      boq: 'BOQ',
      orders: 'Orders',
      claims: 'Claims',
      compliance: 'Compliance',
      draws: 'Bank Draws',
    },
    emptyLine1: 'No Bill of Quantities Created.',
    emptyLine2: 'Start your BOQ by selecting a template or starting from a blank template.',
    contactOrg: 'Reach out to your organisation admin',
    selectTemplate: 'Select a template',
    blankTemplate: 'Start blank template',
    templateModal: {
      title: 'Select a template',
      body: 'Templates are a great starting point for your Bill of Quantity. You’ll find various bank templates in the dropdown below which match the required format and structure for your chosen bank.',
      label: 'Template',
      button: 'Use this template',
    },
    created: {
      title: 'BOQ created',
      content: 'BOQ has been created successfully',
    },
    contextMenu: {
      edit: 'Edit site details',
      view: 'View site details',
      archive: 'Archive site',
      unarchive: 'Unarchive site',
      saveBOQTemplate: 'Save BOQ template',
      delete: 'Delete BOQ',
    },
    statuses: {
      draft: 'Draft BOQ',
      pending: 'Pending approval',
      approved: 'Approved BOQ',
      submit: 'submit',
      decline: 'decline',
      approve: 'approve',
      toast: {
        title: 'Status updated',
        submit: 'BOQ has been submitted',
        approve: 'BOQ has been approved',
        decline: 'BOQ has been declined',
      },
      billChanged: {
        title: 'Nothing was sent',
        body: 'The bill changed before you confirmed. Open it again to submit, approve or decline.',
      },
      confirm: {
        title: '{action} BOQ',
        body: 'Are you sure you want to {action} this BOQ?',
        declineReasonLabel: 'Reason for declining',
        declineReasonPlaceholder: 'Enter a reason for declining this BOQ',
      },
    },
    contractTypes: {
      noContract: 'No Contract',
      fixedCost: 'Fixed Cost',
      costPlus: 'Cost Plus',
    },
    removeItem: 'Remove item',
    boqStatus: 'BOQ Status',
    contractTotal: 'Contract Total',
    costBOQTotal: 'Cost BOQ Total',
    sellingBOQTotal: 'Selling BOQ Total',
    targetMargin: 'Target Margin',
    profitMargin: 'Profit Margin',
    variationTotal: 'Pending Variations',
    totalVariations: 'Total Variations',
    boqBudget: 'BOQ Budget',
    spent: 'Spent',
    remaining: 'Remaining',
    toggleSummary: '{toggle} summary',
    show: 'Show',
    hide: 'Hide',
    submitForApproval: 'Submit for approval',
    awaitingApproval: 'Awaiting approval',
    approve: 'Approve BOQ',
    decline: 'Decline BOQ',
    addToOrder: 'Add to order',
    removeFromOrder: 'Remove from order',
    cannotAddVariationItemToOrder: 'Items with pending variations cannot be added to the cart',
    /**
     * Item-level and deliberately generic: the team rejected distinguishing a deleted vendor from a
     * deleted item, since a hard-deleted item can no longer be traced back to its vendor (PLE-1266).
     */
    cannotAddUnavailableItemToOrder:
      'This item is no longer available from its vendor and cannot be added to the cart. Replace it on the BOQ.',
    unavailableItemsBlockApproval:
      'This BOQ has items that are no longer available. Replace them before it can be approved.',
    /**
     * The same refusal, as a menu item's own description. A dropdown item gives it one line under
     * the label, so it names the blocker and leaves the full sentence to the panel above the bill.
     */
    unavailableItemsBlockApprovalShort: 'Replace the unavailable items first',
    pendingVariation: 'These values are locked until the pending variation is approved or denied.',
    newItemVariation: 'This variation cannot be approved until a spec is specified',
    /**
     * Names the *variation's* item, not the line's — the line itself may be perfectly fine, and it
     * is what approving would put on it that is gone. Deny is the way out, so the copy says so.
     */
    variationItemNoLongerAvailable:
      'The item this variation would add is no longer available, so it cannot be approved. Deny it and pick another item.',
    /** Submitting a variation on an unavailable line that leaves the item as it is. */
    replaceUnavailableItemFirst:
      'This item is no longer available. Pick a replacement item before submitting the variation.',
    /** A rate or quantity change on a line whose own item is gone — approving it fixes nothing. */
    variationKeepsUnavailableItem:
      'This variation keeps the line on an item that is no longer available, so it cannot be approved. Deny it and replace the item.',
    table: {
      lineItem: 'line item',
      group: 'group',
      variation: 'Variation - {type} change',
      value: 'Value',
      spec: 'Spec',
      newItemVariation: 'Variation - New item',
      itemUpdateVariation: 'Variation - Item update',
      /**
       * The banner's own wording — it says what to do about the line, since the line reads as
       * needing action. Which way out is on offer depends on the bill: a draft can drop the line or
       * swap the item, a pending one has to go back to draft first, and an approved one can only
       * have the item swapped.
       */
      itemNoLongerAvailable: 'Item no longer available - replace this item',
      itemNoLongerAvailableRemovable: 'Item no longer available - replace or remove this item',
      itemNoLongerAvailablePending:
        'Item no longer available - return this BOQ to draft to replace or remove it',
      qtyTooLow: 'Qty is less than ordered qty',
      negativeValue: 'Negative values are not allowed',
      oneNewItemAtATime: 'Only one new item can be added at a time.',
      ordered: 'Ordered',
      remaining: 'Remaining',
      approve: 'Approve',
      deny: 'Deny',
      drawNo: 'Draw No',
      columns: {
        number: 'Nr',
        itemName: 'Item name',
        specification: 'Spec',
        vendor: 'Vendor',
        reference: 'Reference',
        type: 'Type',
        unit: 'Unit',
        quantity: 'Qty',
        rate: 'Rate',
        amount: 'Amount',
        remaining: 'Remaining',
        markup: 'Markup %',
        claimPeriod: 'Claim period',
        start: 'Start',
        end: 'End',
        startDate: 'Start date',
        endDate: 'End date',
      },
      contextMenu: {
        details: '{type} details',
        delete: 'Delete {type}',
        edit: 'Edit {type}',
        addGroup: 'Add group',
      },
      sectionDescription:
        'This value is the percentage of the budget which this section occupies. A red value indicates the section has exceeded it’s budget threshold.',
    },
    orderHistorySidebar: {
      title: 'Line Item History',
      noHistory: {
        title: 'No history for this line item has been created.',
        content:
          'As you place orders and claims for this line item, the history will appear here. This will also cover all contra charges made on this line item when submitting claims.',
      },
      contraCharge: 'Contra Charge',
      order: 'Order',
      claim: 'Claim',
      return: 'Return',
      table: {
        headers: {
          createdAt: 'Placed on',
          type: 'Type',
          vendor: 'Vendor',
          markup: 'Markup',
          total: 'Total',
          totalDelivered: 'Total delivered',
          createdBy: 'Created by',
          approvedBy: 'Approved by',
          approvedOn: 'Approved on',
        },
      },
    },
    logHistory: {
      title: 'Log history',
      tableHeaders: {
        placedOn: 'Placed on',
        type: 'Type',
        unit: 'Unit',
        qty: 'Qty',
        rate: 'Rate',
        total: 'Total',
        createdBy: 'Created by',
        approvedBy: 'Approved by',
        approvedOn: 'Approved on',
      },
      delivery: 'Delivery',
      undeliveredItems: 'Undelivered items',
      undeliveredReturnedItems: 'Undelivered returned items',
    },
    filters: {
      typeLabel: 'Type: {value}',
      searchLabel: 'Search: {value}',
    },
  },
  orders: {
    orders: 'orders',
    claims: 'claims',
    emptyLine1: 'You currently have no site {type}',
  },
  compliance: {
    title: 'Compliance Documentation',
    subtitle:
      'Please keep compliance documentation up to date to ensure projects remain compliant.',
    noDraws: 'There are no draws for this site',
    drawFilterLabel: 'Filter by draw',
    emptyLine1: 'Upload your compliance documentation',
    emptyLine2: `Here you can store all your compliance documentation for this site's draws. You can access a list of all compliance templates using the “compliance templates” button.`,
    upload: 'Upload compliance document',
    viewTemplates: 'View compliance templates',
    templates: 'Compliance templates',
  },
  claims: {
    title: 'Bank Claims',
    subtitle: 'Complete bank claim amounts',
    edit: 'Edit draws',
    closeEdit: 'Close edit',
    startDate: 'Planned commencement date',
    endDate: 'Planned completion date',
    startDateLockedByOrders:
      'The commencement date cannot be changed once orders have been placed on this site.',
    startClaim: 'Start claim',
    continueClaim: 'Continue claim',
    download: 'Download',
    downloadSummary: 'Download claim sheet',
    downloadSummaryInfo:
      'Download the bank claim sheet for this site. This sheet will include any up to date information added in the below draw fields with calculated values.',
    previousTotal: 'Previous claim total',
    currentTotal: 'Current claim total',
    balance: 'Balance',
    delete: 'Delete claim',
    complete: 'Complete claim',
    claim: 'Claim',
    claimSheet: 'Claim sheet',
    totalExceedsRemaining: 'Cannot exceed remaining %',
    confirmCompleteModal: {
      content:
        'Once this claim is completed, a claim sheet will be created and added to your site compliance tab. This is final and changes cannot be made after completing a claim. Are you sure?',
      confirm: 'Yes, complete claim',
    },
    deleteClaimModal: {
      content:
        'This will delete this claim and reset it. You can restart the claim from your site bank claims. Are you sure?',
      confirm: 'Yes, delete',
    },
    claimTable: {
      headers: {
        section: 'Section',
        threshold: 'Threshold',
        valuation: 'Valuation %',
        contractAmount: 'Contract amount',
        previousClaim: 'Previous claim',
        remaining: 'Remaining',
        claimingNow: 'Claiming now',
        amountNow: 'Amount now',
      },
      thresholdInfo:
        'Thresholds on sections and subsections are the valuation limits set by your bank template or manually set in your BOQ',
      completeSuccessToast: {
        title: 'Claim completed',
        content: 'Claim has been completed successfully',
      },
      deleteSuccessToast: {
        title: 'Claim deleted',
        content: 'Claim has been deleted successfully',
      },
      downloadSuccessToast: {
        title: '{claim} downloaded',
        content: '{claim} has been downloaded successfully',
      },
    },
    skipClaimPeriodModal: {
      title: 'Skip claim period(s)?',
      content:
        'You are attempting to skip one or more claim periods.\nBy skipping claim periods, you will not be able to initiate claims for the missed periods. Do you wish to continue?',
      confirm: 'Yes, skip',
    },
    loadingModal: {
      title: 'Gathering claims data',
      content:
        'This can take up to 20 seconds.\nYour claim sheet will open as soon this completes.',
    },
    successToast: {
      title: 'Claim generated',
      content: 'Claim has been generated successfully',
    },
    errorToast: {
      title: 'Claim generation failed',
      content: 'Something went wrong, please try again.',
    },
    table: {
      amount: 'Amount',
      datePlaceholder: 'dd/mm/yyyy',
      headers: {
        name: 'Item',
        submittedDate: 'Submitted Date',
        projectedAmount: 'Projected cash in',
        receivedDate: 'Received Date',
        actualAmount: 'Actual cash in',
      },
    },
    updateSuccess: {
      title: 'Draw updated',
      content: 'Draw updated successfully',
    },
    emptyLine1: 'You don’t have permission to view this page.',
  },
  welcomeDialog: {
    title: 'Welcome to your new site',
    subtitle: `Let's get started. What would you like to do first?`,
    buttonOne: 'Invite site members',
    buttonTwo: 'Start your boq',
    buttonThree: 'Edit site details',
  },
  emptyStates: {
    noItemsInCategory: `There are no items to display in the '{category}' category.`,
    noItemsInSearchResult: 'No results found for your search.',
  },
};
